import { type ChainId, ChainIds, defaultDerivationPathTemplates } from "@umami/chains";
import { z } from "zod";

const EVM_DERIVATION_PATH_REGEX = /^m\/44'\/60'\/\d+'\/\d+\/\d+$/;
const TEZOS_DERIVATION_PATH_REGEX = /^(m\/)?44'\/1729'\/\d+'\/\d+'(\/\d+')?$/;

// Bitcoin path unlike other chains can have different path purpose and coin type
// purpose' (44 - legacy, 84 - native segwit, 86 - taproot)
// coin type' (0 - bitcoin, 1 - bitcoin testnet)
// for now we only support native segwit and taproot
// https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki
const BITCOIN_DERIVATION_PATH_REGEX = /^m\/(84|86)'\/(0|1)'\/\d+'\/\d+\/\d+$/;

const ERROR_MESSAGES = {
  INVALID_EVM_PATH: "Invalid derivation path. Example: m/44'/60'/0'/0/0",
  INVALID_TEZOS_PATH: "Invalid derivation path. Example: m/44'/1729'/0'/0'",
  INVALID_BITCOIN_PATH: "Invalid derivation path. Example: m/84'/0'/0'/0/0",
};

const isTemplate = (value: string): boolean =>
  Object.values(defaultDerivationPathTemplates).includes(value);

const createDerivationPathValidator = (regex: RegExp, errorMessage: string) =>
  z
    .string()
    .refine(value => isTemplate(value) || regex.test(value), { message: errorMessage })
    .optional()
    .or(z.literal(""));

export const ethereumDerivationPathValidator = createDerivationPathValidator(
  EVM_DERIVATION_PATH_REGEX,
  ERROR_MESSAGES.INVALID_EVM_PATH
);

export const etherlinkDerivationPathValidator = createDerivationPathValidator(
  EVM_DERIVATION_PATH_REGEX,
  ERROR_MESSAGES.INVALID_EVM_PATH
);

export const tezosDerivationPathValidator = createDerivationPathValidator(
  TEZOS_DERIVATION_PATH_REGEX,
  ERROR_MESSAGES.INVALID_TEZOS_PATH
);

export const bitcoinDerivationPathValidator = createDerivationPathValidator(
  BITCOIN_DERIVATION_PATH_REGEX,
  ERROR_MESSAGES.INVALID_BITCOIN_PATH
);

export const chainValidators = {
  [ChainIds.ETHEREUM]: ethereumDerivationPathValidator,
  [ChainIds.ETHERLINK]: etherlinkDerivationPathValidator,
  [ChainIds.TEZOS]: tezosDerivationPathValidator,
  [ChainIds.BSC]: ethereumDerivationPathValidator,
  [ChainIds.BITCOIN]: bitcoinDerivationPathValidator,
} as const;

export const getChainValidator = (chainId: ChainId) => chainValidators[chainId];
